import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { gtmEventNames } from '../../../constants/gtm-tracking.constants';
import { IGtmDataLayerEntry, IGtmEventData } from '../../interfaces/gtm-tracking';
import { GtmTracking } from './gtm/gtm-tracking';
import { TrackingData } from '../../interfaces/tracking';
import { NativeAppService } from '@shared/common/services/native-app/native-app.service';

@Injectable({
  providedIn: 'root'
})
export class TrackingService {
  gtmTracking: GtmTracking;

  constructor(
    private readonly nativeAppService: NativeAppService,
    private readonly router: Router,
    @Inject(PLATFORM_ID) private readonly platformId: any
  ) {
    this.gtmTracking = new GtmTracking(nativeAppService, platformId);
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.gtmTracking.fakeDataLayer = [];
      }
    });
  }

  /**
   * It enables tracking using Google Tag Manager
   */
  enableTracking() {
    this.gtmTracking.enableTracking();
  }

  /**
   * It tracks an event using GTM Tracking
   * @param eventName This is the name of the event you want to track
   * @param gtmData This is the data that you want to pass to GTM
   */
  gtmTrackEvent(eventName: gtmEventNames, gtmData: TrackingData) {
    this.gtmTracking.trackEvent(eventName, { gtmData });
  }

  /**
   * Add some new data to the dataLayer
   */
  trackGtmData(data: IGtmDataLayerEntry): void {
    this.gtmTracking.track(data);
  }

  /**
   * Tracks a custom GA event.
   *
   * Important: Only use this if you cannot use a semantic GTM event, this is just a loophole for quick and dirty cases.
   *
   * Non-Interaction can be set to true to not count events as user interaction (e.g. interaction events fired on a landingpage might mess up bounce rates).
   */
  trackGaEvent(
    eventCategory: string,
    eventAction: string,
    eventLabel?: string,
    eventValue?: number,
    eventNonInteraction?: boolean,
    callback?: Function
  ): void {
    this.gtmTracking.trackEvent(
      gtmEventNames.gaEvent,
      {
        gaEventCategory: eventCategory,
        gaEventAction: eventAction,
        gaEventLabel: eventLabel,
        gaEventValue: eventValue,
        gaEventNonInteraction: eventNonInteraction
      },
      callback
    );
  }

  /**
   * Trigger a GTM event.
   *
   * The data sent will we automatically removed from the dataLayer after the event was triggered.
   * To add permanent data to the dataLayer use trackGtmData before calling trackGtmEvent.
   *
   * The given callback is triggered after all tags for this event were fired.
   */
  trackGtmEvent(eventName: gtmEventNames, data: IGtmEventData, callback?: Function): void {
    this.gtmTracking.trackEvent(eventName, data, callback);
  }
}
